/**
 * COD design layer for Horizon: card Add to Cart, Buy Now, sticky buy bar, bundle offers,
 * pincode check, hero slider, tabs and the bottom-nav cart count.
 * Loaded as a module from layout/theme.liquid. Every feature degrades to plain links/forms
 * without JavaScript.
 */
import { formatMoney } from '@theme/money-formatting';

const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @returns {HTMLElement & { open?: () => void } | null} */
function cartDrawer() {
  return document.querySelector('theme-drawer#cart-drawer');
}

function isCartDrawerOpen() {
  const dialog = cartDrawer()?.querySelector('dialog');
  return Boolean(dialog?.open);
}

async function openCart() {
  if (isCartDrawerOpen()) return;
  const actions = window.Shopify?.actions;
  if (actions?.openCart) {
    await actions.openCart();
  } else if (cartDrawer()?.open) {
    cartDrawer()?.open?.();
  } else {
    window.location.href = window.Theme?.routes?.cart_url ?? '/cart';
  }
}

/* ---------------------------------------------------------------------------------------------
 * Product card "Add to Cart"
 * ------------------------------------------------------------------------------------------- */

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showCardError(form, message) {
  let note = form.querySelector('.cod-card-error');
  if (!note) {
    note = document.createElement('p');
    note.className = 'cod-card-error';
    note.setAttribute('role', 'alert');
    form.append(note);
  }
  note.textContent = message;
  setTimeout(() => note?.remove(), 4000);
}

/** @param {HTMLButtonElement} button */
function flashAdded(button) {
  const label = button.querySelector('[data-cod-label]');
  if (!label) return;
  const original = label.textContent;
  label.textContent = button.dataset.addedLabel || 'Added';
  setTimeout(() => {
    label.textContent = original;
  }, 1200);
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-cod-card-add]')) return;
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  const variantId = new FormData(form).get('id');
  const quantity = Number(new FormData(form).get('quantity')) || 1;
  if (!variantId) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const actions = window.Shopify?.actions;
    if (actions?.updateCart) {
      const result = await actions.updateCart({
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
      });
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      flashAdded(button);
      // Horizon auto-opens the drawer on "add"; open it ourselves when it didn't (e.g. first item).
      await wait(150);
      await openCart();
    } else {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart');
      window.location.href = window.Theme?.routes?.cart_url ?? '/cart';
    }
  } catch (error) {
    showCardError(form, error instanceof Error ? error.message : 'Could not add to cart');
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------------------------------------
 * Buy Now (product page): checkout with only this variant and quantity
 * ------------------------------------------------------------------------------------------- */

/** @param {HTMLFormElement} form */
function selectedLine(form) {
  const idInput = form.querySelector('input[name="id"]');
  const id = idInput instanceof HTMLInputElement ? idInput.value : '';
  const quantityInput = form.querySelector('input[name="quantity"]');
  const quantity = Math.max(1, parseInt(quantityInput instanceof HTMLInputElement ? quantityInput.value : '1', 10) || 1);
  return { id, quantity };
}

/** @param {HTMLFormElement} form */
function canBuy(form) {
  const add = form.querySelector('[ref="addToCartButton"]');
  return !(add instanceof HTMLButtonElement && add.disabled);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const sticky = target.closest('[data-cod-sticky-trigger]');
  if (sticky) {
    event.preventDefault();
    sticky.closest('.cod-buy-now-wrap')?.querySelector('[data-cod-buy-now]')?.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true })
    );
    return;
  }

  const button = target.closest('[data-cod-buy-now]');
  if (!(button instanceof HTMLButtonElement)) return;
  const form = button.form;
  if (!form) return;
  // Without this, Horizon's form handler would add to cart and open the drawer instead.
  event.preventDefault();
  if (button.disabled || !canBuy(form)) return;

  const { id, quantity } = selectedLine(form);
  if (!id) return;
  button.setAttribute('aria-busy', 'true');
  const cartUrl = button.dataset.cartUrl || '/cart';
  window.location.href = `${cartUrl}/${encodeURIComponent(id)}:${quantity}`;
});

/** Keep Buy Now enabled/disabled in step with Horizon's Add to Cart button (variant changes). */
function syncBuyNow(root = document) {
  root.querySelectorAll('[data-cod-buy-now]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement) || !button.form) return;
    const disabled = !canBuy(button.form);
    // Only write on change: the observer below watches `disabled`, so a blind write would loop.
    if (button.disabled !== disabled) button.disabled = disabled;
    const stickyButton = button.closest('.cod-buy-now-wrap')?.querySelector('[data-cod-sticky-trigger]');
    if (stickyButton instanceof HTMLButtonElement && stickyButton.disabled !== disabled) stickyButton.disabled = disabled;
  });
}

const buyNowObserver = new MutationObserver(() => syncBuyNow());
function watchBuyNow() {
  document.querySelectorAll('[data-cod-buy-now]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement) || !button.form) return;
    buyNowObserver.observe(button.form, { attributes: true, attributeFilter: ['disabled'], subtree: true });
  });
  syncBuyNow();
}

/* ---------------------------------------------------------------------------------------------
 * Sticky Buy Now bar (phones)
 * ------------------------------------------------------------------------------------------- */

function initStickyBar() {
  const sticky = document.querySelector('[data-cod-sticky]');
  if (!(sticky instanceof HTMLElement) || sticky.closest('dialog')) return;
  const buttons = sticky.closest('.product-form-buttons') ?? sticky.parentElement;
  if (!buttons) return;

  const priceTarget = sticky.querySelector('[data-cod-sticky-price]');
  const syncPrice = () => {
    const price = document.querySelector('.product-details product-price .price bdi, .product-information product-price .price bdi');
    if (price && priceTarget) priceTarget.textContent = price.textContent?.trim() ?? '';
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.hidden = !show;
      sticky.querySelector('[data-cod-sticky-trigger]')?.setAttribute('tabindex', show ? '0' : '-1');
      document.body.classList.toggle('cod-has-sticky-buy', show);
      if (show) syncPrice();
    },
    { threshold: 0 }
  );
  observer.observe(buttons);
}

/* ---------------------------------------------------------------------------------------------
 * Bundle offers: choosing a bundle sets the product form quantity
 * ------------------------------------------------------------------------------------------- */

class CodBundleOffers extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.addEventListener('click', this.#onClick);
    this.form = this.closest('.product-details, .product-information, product-form-component')
      ?.querySelector('form[data-type="add-to-cart-form"]') ?? document.querySelector('form[data-type="add-to-cart-form"]');
    this.priceObserver = new MutationObserver(() => this.#refresh());
    const price = document.querySelector('.product-details product-price, .product-information product-price');
    if (price) this.priceObserver.observe(price, { childList: true, subtree: true });
    this.#syncFromQuantity();
    this.quantityListener = () => this.#syncFromQuantity();
    this.form?.addEventListener('change', this.quantityListener);
    this.form?.addEventListener('input', this.quantityListener);
    this.form?.addEventListener('click', this.quantityListener);
  }

  disconnectedCallback() {
    this.priceObserver?.disconnect();
    if (this.quantityListener) {
      this.form?.removeEventListener('change', this.quantityListener);
      this.form?.removeEventListener('input', this.quantityListener);
      this.form?.removeEventListener('click', this.quantityListener);
    }
  }

  #readVariants() {
    try {
      return JSON.parse(this.querySelector('[data-cod-variants]')?.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  get #quantityInput() {
    const input = this.form?.querySelector('input[name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const option = event.target instanceof Element ? event.target.closest('.cod-bundle') : null;
    if (!(option instanceof HTMLElement)) return;
    const quantity = Number(option.dataset.quantity) || 1;
    const input = this.#quantityInput;
    if (input) {
      const max = Number(input.max) || Infinity;
      input.value = String(Math.min(quantity, max));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#select(quantity);
  };

  /** @param {number} quantity */
  #select(quantity) {
    this.querySelectorAll('.cod-bundle').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      option.setAttribute('aria-checked', String(Number(option.dataset.quantity) === quantity));
    });
  }

  #syncFromQuantity() {
    requestAnimationFrame(() => {
      const quantity = Number(this.#quantityInput?.value) || 1;
      this.#select(quantity);
    });
  }

  #refresh() {
    const idInput = this.form?.querySelector('input[name="id"]');
    const id = idInput instanceof HTMLInputElement ? idInput.value : '';
    const variant = this.variants?.[id];
    if (!variant) return;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'INR';
    const money = (/** @type {number} */ cents) => formatMoney(Math.round(cents), format, currency).replace(/\.00(?!\d)/, '');
    const template = this.dataset.saveLabel || 'You save [amount]';

    this.querySelectorAll('.cod-bundle').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      const quantity = Number(option.dataset.quantity) || 1;
      const percent = Number(option.dataset.percent) || 0;
      const line = variant.price * quantity;
      const discount = Math.floor((line * percent) / 100);
      const mrpSaving = variant.compare > variant.price ? (variant.compare - variant.price) * quantity : 0;
      const saved = discount + mrpSaving;
      const total = option.querySelector('[data-cod-total]');
      if (total) total.textContent = money(line - discount);
      const full = option.querySelector('[data-cod-full]');
      if (full) full.textContent = money(Math.max(variant.compare, variant.price) * quantity);
      const save = option.querySelector('[data-cod-save]');
      if (save instanceof HTMLElement) {
        save.hidden = saved <= 0;
        save.textContent = template.replace('[amount]', money(saved));
      }
    });
  }
}

/* ---------------------------------------------------------------------------------------------
 * Pincode check
 * ------------------------------------------------------------------------------------------- */

/**
 * @param {Date} from
 * @param {number} days
 * @param {boolean} skipSunday
 */
function addDeliveryDays(from, days, skipSunday) {
  const date = new Date(from);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    if (skipSunday && date.getDay() === 0) continue;
    added += 1;
  }
  return date;
}

class CodPincodeCheck extends HTMLElement {
  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);
    const input = this.querySelector('input');
    input?.addEventListener('input', () => {
      if (input instanceof HTMLInputElement) input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });
  }

  /** @param {string} name */
  #text(name) {
    return this.querySelector(`[data-cod-text-${name}]`)?.textContent?.trim() ?? '';
  }

  /** @param {string} pincode */
  #blocked(pincode) {
    const list = (this.dataset.blocked || '')
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    return list.some((prefix) => pincode.startsWith(prefix));
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const input = this.querySelector('input');
    const result = this.querySelector('.cod-pincode__result');
    const button = this.querySelector('button');
    if (!(input instanceof HTMLInputElement) || !(result instanceof HTMLElement)) return;
    const pincode = input.value.trim();
    result.hidden = false;
    result.classList.remove('is-error');

    if (!/^[1-9]\d{5}$/.test(pincode)) {
      result.classList.add('is-error');
      result.textContent = this.#text('invalid');
      return;
    }
    if (this.#blocked(pincode)) {
      result.classList.add('is-error');
      result.textContent = this.#text('blocked');
      return;
    }

    if (button instanceof HTMLButtonElement) button.disabled = true;
    let place = '';
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`, { signal: controller.signal });
      clearTimeout(timer);
      const data = await response.json();
      const entry = Array.isArray(data) ? data[0] : null;
      if (entry?.Status === 'Error' || (entry?.Status === 'Success' && !entry.PostOffice?.length)) {
        result.classList.add('is-error');
        result.textContent = this.#text('invalid');
        return;
      }
      const office = entry?.PostOffice?.[0];
      if (office) place = [office.District, office.State].filter(Boolean).join(', ');
    } catch {
      // Lookup service unreachable: still show the delivery promise.
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }

    const maxDays = Number(this.dataset.maxDays) || 6;
    const skipSunday = this.dataset.skipSunday === 'true';
    const date = addDeliveryDays(new Date(), maxDays, skipSunday).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'Asia/Kolkata',
    });
    const template = this.querySelector('template[data-cod-ok]');
    if (!(template instanceof HTMLTemplateElement)) return;
    result.replaceChildren(template.content.cloneNode(true));
    const message = result.querySelector('[data-cod-message]');
    if (message) message.textContent = this.#text('delivery').replace('[date]', date).replace('[pincode]', pincode);
    const placeNode = result.querySelector('[data-cod-place]');
    if (placeNode instanceof HTMLElement) {
      placeNode.textContent = place;
      placeNode.hidden = !place;
    }
  };
}

/* ---------------------------------------------------------------------------------------------
 * Hero slider: dots + autoplay (pauses on touch/hover and when the tab is hidden)
 * ------------------------------------------------------------------------------------------- */

class CodHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cod-track]');
    this.dots = [...this.querySelectorAll('[data-cod-dot]')];
    this.paused = false;
    if (!(this.track instanceof HTMLElement) || this.dots.length < 2) return;

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerdown', this.#pause);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.dots.forEach((dot) => dot.addEventListener('click', this.#onDot));

    const seconds = Number(this.dataset.autoplay) || 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (seconds > 0 && !reduced) {
      this.timer = setInterval(() => {
        if (this.paused || document.hidden || !(this.track instanceof HTMLElement)) return;
        this.#go((this.#index() + 1) % this.dots.length);
      }, seconds * 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  #index() {
    if (!(this.track instanceof HTMLElement)) return 0;
    return Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
  }

  /** @param {number} index */
  #go(index) {
    if (!(this.track instanceof HTMLElement)) return;
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: rtl * index * this.track.clientWidth, behavior: 'smooth' });
  }

  #onScroll = () => {
    const index = Math.abs(this.#index());
    this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));
  };

  /** @param {Event} event */
  #onDot = (event) => {
    const dot = event.currentTarget;
    if (dot instanceof HTMLElement) this.#go(Number(dot.dataset.codDot) || 0);
  };

  #pause = () => {
    this.paused = true;
  };

  #resume = () => {
    this.paused = false;
  };
}

/* ---------------------------------------------------------------------------------------------
 * Tabs (Our Top Sellers)
 * ------------------------------------------------------------------------------------------- */

class CodTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  /** @param {Element} tab */
  #activate(tab, focus = false) {
    this.#tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    });
    if (focus && tab instanceof HTMLElement) tab.focus();
    tab.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab) this.#activate(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {Element} */ (document.activeElement));
    if (current === -1) return;
    let next = -1;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === -1) return;
    event.preventDefault();
    const tab = tabs[next];
    if (tab) this.#activate(tab, true);
  };
}

/* ---------------------------------------------------------------------------------------------
 * Bottom nav: cart count + open the drawer
 * ------------------------------------------------------------------------------------------- */

/** @param {number} count */
function renderCartCount(count) {
  document.querySelectorAll('[data-cod-cart-count]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.textContent = count > 99 ? '99+' : String(count);
    node.hidden = count === 0;
  });
}

document.addEventListener(CART_LINES_UPDATE, (event) => {
  /** @type {{ promise?: Promise<any> }} */
  const detail = /** @type {any} */ (event);
  detail.promise
    ?.then(({ cart, detail: extra } = {}) => {
      const count = cart?.totalQuantity ?? extra?.itemCount;
      if (typeof count === 'number') renderCartCount(count);
    })
    .catch(() => {});
});

window.addEventListener('pageshow', async (event) => {
  if (!event.persisted || !document.querySelector('[data-cod-cart-count]')) return;
  try {
    const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`);
    const cart = await response.json();
    renderCartCount(Number(cart.item_count) || 0);
  } catch {
    // Keep the server-rendered count.
  }
});

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-cod-open-cart]') : null;
  if (!link || !cartDrawer()) return;
  event.preventDefault();
  openCart();
});

/* ---------------------------------------------------------------------------------------------
 * Boot
 * ------------------------------------------------------------------------------------------- */

if (!customElements.get('cod-bundle-offers')) customElements.define('cod-bundle-offers', CodBundleOffers);
if (!customElements.get('cod-pincode-check')) customElements.define('cod-pincode-check', CodPincodeCheck);
if (!customElements.get('cod-hero')) customElements.define('cod-hero', CodHero);
if (!customElements.get('cod-tabs')) customElements.define('cod-tabs', CodTabs);

function boot() {
  watchBuyNow();
  initStickyBar();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', () => {
  watchBuyNow();
  initStickyBar();
});
